/**
 * RulesPage.js - COMPLETE COMMUNICATION RULES EDITOR
 * 
 * Single configuration interface for ALL automation rules.
 * Exposes every setting supported by the backend.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { API } from '../../../lib/api';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, Edit, Trash2, RefreshCw, Brain, Play, Pause, Copy,
  MessageSquare, Bell, Mail, Clock, Calendar, CheckCircle,
  Settings, Database, X, Save, Zap, AlertTriangle, Archive,
  ChevronDown, ChevronRight, TestTube, History, Filter,
  Phone, Globe, Home, CreditCard, Users, FileText, Eye,
  Smartphone, Send, Target, Shield, Timer, GitBranch
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../components/ui/accordion';
import {
  Alert,
  AlertDescription,
} from '../../../components/ui/alert';

// ============================================================================
// CONSTANTS & LABELS
// ============================================================================

const EVENT_LABELS = {
  booking_created: 'Prenotazione Creata',
  booking_confirmed: 'Prenotazione Confermata',
  arrival_7_days: 'Arrivo -7 giorni',
  arrival_3_days: 'Arrivo -3 giorni',
  arrival_1_day: 'Arrivo -1 giorno',
  arrival_today: 'Arrivo Oggi',
  online_checkin_completed: 'Check-in Online Completato',
  guest_checked_in: 'Ospite Arrivato',
  guest_checked_out: 'Ospite Partito',
  checkin_missing: 'Check-in Mancante',
  checkin_completed: 'Check-in Completato',
  access_code_ready: 'Codice Accesso Pronto',
  checkout_today: 'Checkout Oggi',
  checkout_completed: 'Checkout Completato',
  review_request: 'Richiesta Recensione',
  birthday: 'Compleanno',
  payment_pending: 'Pagamento in Attesa',
  payment_received: 'Pagamento Ricevuto',
  manual: 'Manuale',
};

const EXECUTION_MODE_LABELS = {
  immediate: 'Immediato',
  relative: 'Relativo',
  fixed_time: 'Orario Fisso',
};

const MISSED_POLICY_LABELS = {
  skip: 'Salta',
  send_immediately: 'Invia Subito',
  send_after_delay: 'Invia dopo X minuti',
  reschedule: 'Riprogramma',
  manual_review: 'Revisione Manuale',
};

const BOOKING_SOURCE_LABELS = {
  'booking.com': 'Booking.com',
  'airbnb': 'Airbnb',
  'direct': 'Diretto',
  'facebook': 'Facebook',
  'manual': 'Manuale',
  'ota': 'Altro OTA',
};

const CHANNEL_ICONS = {
  whatsapp: MessageSquare,
  push: Bell,
  email: Mail,
  sms: Smartphone,
};

const DEFAULT_RULES = [
  { name: 'Conferma Prenotazione', event: 'booking_confirmed' },
  { name: 'Promemoria Arrivo -3 giorni', event: 'arrival_3_days' },
  { name: 'Messaggio Arrivo', event: 'arrival_today' },
  { name: 'Codice Accesso', event: 'access_code_ready' },
  { name: 'Benvenuto', event: 'guest_checked_in' },
  { name: 'Checkout', event: 'checkout_today' },
  { name: 'Grazie', event: 'checkout_completed' },
  { name: 'Richiesta Recensione', event: 'review_request' },
  { name: 'Auguri Compleanno', event: 'birthday' },
  { name: 'Promozione Ritorno', event: 'manual' },
];

// ============================================================================
// RULE CARD COMPONENT
// ============================================================================

const RuleCard = ({ rule, onEdit, onDelete, onToggle, onDuplicate, onTest, hasOverlap, overlapInfo }) => {
  const isDisabled = !rule.enabled;
  const disabledReason = rule.disabled_reason;
  
  return (
    <div 
      className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all hover:shadow-md ${
        isDisabled ? 'border-gray-100 opacity-60' : 
        hasOverlap ? 'border-amber-300 ring-1 ring-amber-200' : 
        'border-gray-200'
      }`}
      data-testid={`rule-card-${rule.id}`}
    >
      {/* Overlap Warning */}
      {hasOverlap && rule.enabled && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="text-xs text-amber-700">Overlap: {overlapInfo}</span>
        </div>
      )}
      
      {/* Disabled Reason */}
      {isDisabled && disabledReason && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
          <Archive className="w-4 h-4 text-gray-500" />
          <span className="text-xs text-gray-600">
            {disabledReason === 'DUPLICATE_OVERLAP' ? 'Disabilitato: regola duplicata' :
             disabledReason === 'OVERLAP_WITH_BOOKING_CREATED' ? 'Disabilitato: coperto da booking_created' :
             'Disabilitato'}
          </span>
        </div>
      )}
      
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">{rule.name}</h3>
              <Badge variant={rule.enabled ? "default" : "secondary"} className="text-xs">
                {rule.enabled ? 'Attiva' : 'Inattiva'}
              </Badge>
            </div>
            {rule.description && (
              <p className="text-xs text-gray-400 mt-0.5">{rule.description}</p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              {EVENT_LABELS[rule.event] || rule.event}
            </p>
          </div>
          <button
            onClick={() => onToggle(rule)}
            className={`p-2 rounded-lg transition-colors ${rule.enabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            title={rule.enabled ? 'Disattiva' : 'Attiva'}
            data-testid={`toggle-rule-${rule.id}`}
          >
            {rule.enabled ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>
        </div>
        
        {/* Channels */}
        <div className="flex gap-2 mt-3 flex-wrap">
          {rule.channels?.map(ch => {
            const Icon = CHANNEL_ICONS[ch] || MessageSquare;
            return (
              <span key={ch} className="px-2 py-1 bg-gray-100 rounded-lg text-xs flex items-center gap-1 text-gray-700">
                <Icon className="w-3 h-3" />
                {ch}
              </span>
            );
          })}
          {rule.simulation_mode && (
            <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
              Simulazione
            </Badge>
          )}
        </div>

        {/* Key Settings Summary */}
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-2 text-gray-500">
            <Clock className="w-4 h-4" />
            <span>
              {rule.timing?.execution_mode === 'fixed_time' 
                ? `Ore ${rule.timing.fixed_time}` 
                : rule.timing?.execution_mode === 'relative'
                ? `${rule.timing.offset_days >= 0 ? '+' : ''}${rule.timing.offset_days}g da ${rule.timing.reference_event}`
                : `Delay: ${rule.delay_minutes || 0} min`}
            </span>
          </div>
          <div className="flex items-center gap-2 text-gray-500">
            <Zap className="w-4 h-4" />
            <span>Priorità: {rule.priority || 5}</span>
          </div>
        </div>

        {/* Missed Execution Policy */}
        {rule.missed_execution && (
          <div className="mt-2 text-xs text-blue-600 flex items-center gap-1">
            <Timer className="w-3 h-3" />
            <span>Se mancato: {MISSED_POLICY_LABELS[rule.missed_execution.policy] || rule.missed_execution.policy}</span>
          </div>
        )}

        {rule.template_name && (
          <div className="mt-2 text-sm text-gray-500">
            <FileText className="w-3 h-3 inline mr-1" />
            Template: <span className="text-gray-700">{rule.template_name}</span>
          </div>
        )}

        {rule.quiet_hours?.enabled && (
          <div className="mt-2 text-xs text-amber-600">
            🌙 Quiet Hours: {rule.quiet_hours.start_time || rule.quiet_hours.start} - {rule.quiet_hours.end_time || rule.quiet_hours.end}
          </div>
        )}

        {/* Dependencies */}
        {rule.depends_on?.length > 0 && (
          <div className="mt-2 text-xs text-purple-600 flex items-center gap-1">
            <GitBranch className="w-3 h-3" />
            <span>Dipende da: {rule.depends_on.length} regole</span>
          </div>
        )}

        {/* Stats */}
        {(rule.trigger_count > 0 || rule.messages_sent > 0) && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-400">
            {rule.trigger_count > 0 && (
              <span>Esecuzioni: {rule.trigger_count}</span>
            )}
            {rule.messages_sent > 0 && (
              <span className="text-green-600">Inviati: {rule.messages_sent}</span>
            )}
            {rule.errors_count > 0 && (
              <span className="text-red-600">Errori: {rule.errors_count}</span>
            )}
            {rule.last_triggered && (
              <span>Ultimo: {new Date(rule.last_triggered).toLocaleDateString('it-IT')}</span>
            )}
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
        <div className="flex gap-1">
          <button
            onClick={() => onTest(rule)}
            className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
            title="Testa Regola"
            data-testid={`test-rule-${rule.id}`}
          >
            <TestTube className="w-4 h-4 text-blue-600" />
          </button>
          <button
            onClick={() => onDuplicate(rule)}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            title="Duplica"
            data-testid={`duplicate-rule-${rule.id}`}
          >
            <Copy className="w-4 h-4 text-gray-600" />
          </button>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => onEdit(rule)}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            title="Modifica"
            data-testid={`edit-rule-${rule.id}`}
          >
            <Edit className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={() => onDelete(rule.id)}
            className="p-2 hover:bg-red-100 rounded-lg transition-colors"
            title="Elimina"
            data-testid={`delete-rule-${rule.id}`}
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPLETE RULE EDITOR MODAL
// ============================================================================

const RuleEditorModal = ({ isOpen, onClose, rule, templates, units, allRules, onSave }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [previewMessage, setPreviewMessage] = useState('');
  
  const [formData, setFormData] = useState({
    // General
    name: '',
    description: '',
    event: 'booking_created',
    enabled: true,
    priority: 5,
    
    // Timing
    execution_mode: 'immediate',
    delay_days: 0,
    delay_hours: 0,
    delay_minutes: 0,
    reference_event: 'arrival',
    offset_days: 0,
    fixed_time: '09:00',
    timezone: 'Europe/Rome',
    
    // Missed Execution Policy (CRITICAL)
    missed_policy: 'send_after_delay',
    missed_delay_minutes: 2,
    
    // Channels
    channels: ['whatsapp'],
    fallback_order: [],
    
    // Template
    template_id: '',
    language: 'it',
    
    // Conditions
    booking_sources: [],
    require_valid_phone: false,
    require_booking_active: true,
    require_booking_not_cancelled: true,
    require_online_checkin_completed: false,
    require_entry_code_available: false,
    require_payment_completed: false,
    require_assigned_room: false,
    min_nights: '',
    max_nights: '',
    min_amount: '',
    max_amount: '',
    languages: [],
    nationalities: [],
    unit_ids: [],
    
    // Quiet Hours
    quiet_hours_enabled: true,
    quiet_hours_use_global: true,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    
    // Dependencies
    depends_on: [],
    
    // Retry
    retry_attempts: 3,
    retry_backoff: 30,
    retry_timeout: 60,
    
    // Simulation
    simulation_mode: true,
  });

  // Load rule data when editing
  useEffect(() => {
    if (rule) {
      setFormData({
        name: rule.name || '',
        description: rule.description || '',
        event: rule.event || 'booking_created',
        enabled: rule.enabled !== false,
        priority: rule.priority || 5,
        
        // Timing
        execution_mode: rule.timing?.execution_mode || 'immediate',
        delay_days: rule.timing?.delay_days || 0,
        delay_hours: rule.timing?.delay_hours || 0,
        delay_minutes: rule.timing?.delay_minutes || rule.delay_minutes || 0,
        reference_event: rule.timing?.reference_event || 'arrival',
        offset_days: rule.timing?.offset_days || 0,
        fixed_time: rule.timing?.fixed_time || '09:00',
        timezone: rule.timing?.timezone || 'Europe/Rome',
        
        // Missed Execution
        missed_policy: rule.missed_execution?.policy || 'send_after_delay',
        missed_delay_minutes: rule.missed_execution?.delay_minutes || 2,
        
        // Channels
        channels: rule.channels || ['whatsapp'],
        fallback_order: rule.channel_config?.fallback_order || [],
        
        // Template
        template_id: rule.template_id || '',
        language: rule.language || 'it',
        
        // Conditions
        booking_sources: rule.conditions_config?.booking_sources || rule.conditions?.booking_sources || [],
        require_valid_phone: rule.conditions_config?.require_valid_phone || rule.conditions?.require_valid_phone || false,
        require_booking_active: rule.conditions_config?.require_booking_active ?? rule.conditions?.require_booking_active ?? true,
        require_booking_not_cancelled: rule.conditions_config?.require_booking_not_cancelled ?? rule.conditions?.require_booking_not_cancelled ?? true,
        require_online_checkin_completed: rule.conditions_config?.require_online_checkin_completed || rule.conditions?.require_online_checkin_completed || false,
        require_entry_code_available: rule.conditions_config?.require_entry_code_available || rule.conditions?.require_entry_code_available || false,
        require_payment_completed: rule.conditions_config?.require_payment_completed || rule.conditions?.require_payment_completed || false,
        require_assigned_room: rule.conditions_config?.require_assigned_room || rule.conditions?.require_assigned_room || false,
        min_nights: rule.conditions_config?.min_nights || rule.conditions?.min_nights || '',
        max_nights: rule.conditions_config?.max_nights || rule.conditions?.max_nights || '',
        min_amount: rule.conditions_config?.min_amount || rule.conditions?.min_amount || '',
        max_amount: rule.conditions_config?.max_amount || rule.conditions?.max_amount || '',
        languages: rule.conditions_config?.languages || rule.conditions?.languages || [],
        nationalities: rule.conditions_config?.nationalities || rule.conditions?.nationalities || [],
        unit_ids: rule.conditions_config?.units || rule.conditions?.units || [],
        
        // Quiet Hours
        quiet_hours_enabled: rule.quiet_hours?.enabled ?? true,
        quiet_hours_use_global: rule.quiet_hours?.use_global ?? true,
        quiet_hours_start: rule.quiet_hours?.start_time || rule.quiet_hours?.start || '22:00',
        quiet_hours_end: rule.quiet_hours?.end_time || rule.quiet_hours?.end || '08:00',
        
        // Dependencies
        depends_on: rule.depends_on || [],
        
        // Retry
        retry_attempts: rule.retry_config?.max_attempts || rule.retry_policy?.max_retries || 3,
        retry_backoff: rule.retry_config?.backoff_minutes || rule.retry_policy?.retry_delay_minutes || 30,
        retry_timeout: rule.retry_config?.timeout_minutes || 60,
        
        // Simulation
        simulation_mode: rule.simulation_mode !== false,
      });
    } else {
      // New rule defaults
      setFormData({
        name: '',
        description: '',
        event: 'booking_created',
        enabled: true,
        priority: 5,
        execution_mode: 'immediate',
        delay_days: 0,
        delay_hours: 0,
        delay_minutes: 0,
        reference_event: 'arrival',
        offset_days: 0,
        fixed_time: '09:00',
        timezone: 'Europe/Rome',
        missed_policy: 'send_after_delay',
        missed_delay_minutes: 2,
        channels: ['whatsapp'],
        fallback_order: [],
        template_id: templates[0]?.id || '',
        language: 'it',
        booking_sources: [],
        require_valid_phone: false,
        require_booking_active: true,
        require_booking_not_cancelled: true,
        require_online_checkin_completed: false,
        require_entry_code_available: false,
        require_payment_completed: false,
        require_assigned_room: false,
        min_nights: '',
        max_nights: '',
        min_amount: '',
        max_amount: '',
        languages: [],
        nationalities: [],
        unit_ids: [],
        quiet_hours_enabled: true,
        quiet_hours_use_global: true,
        quiet_hours_start: '22:00',
        quiet_hours_end: '08:00',
        depends_on: [],
        retry_attempts: 3,
        retry_backoff: 30,
        retry_timeout: 60,
        simulation_mode: true,
      });
    }
    setActiveTab('general');
  }, [rule, templates]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayField = (field, value) => {
    setFormData(prev => {
      const arr = prev[field] || [];
      if (arr.includes(value)) {
        return { ...prev, [field]: arr.filter(v => v !== value) };
      }
      return { ...prev, [field]: [...arr, value] };
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Inserisci un nome per la regola');
      return;
    }
    if (!formData.template_id) {
      toast.error('Seleziona un template');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        event: formData.event,
        enabled: formData.enabled,
        priority: parseInt(formData.priority) || 5,
        
        // Timing
        timing: {
          execution_mode: formData.execution_mode,
          delay_days: parseInt(formData.delay_days) || 0,
          delay_hours: parseInt(formData.delay_hours) || 0,
          delay_minutes: parseInt(formData.delay_minutes) || 0,
          reference_event: formData.reference_event,
          offset_days: parseInt(formData.offset_days) || 0,
          fixed_time: formData.fixed_time,
          timezone: formData.timezone,
        },
        delay_minutes: (parseInt(formData.delay_days) || 0) * 1440 + 
                       (parseInt(formData.delay_hours) || 0) * 60 + 
                       (parseInt(formData.delay_minutes) || 0),
        
        // Missed Execution
        missed_execution: {
          policy: formData.missed_policy,
          delay_minutes: parseInt(formData.missed_delay_minutes) || 2,
        },
        
        // Channels
        channels: formData.channels,
        channel_config: {
          primary: formData.channels[0] || 'whatsapp',
          fallback_order: formData.fallback_order,
        },
        
        // Template
        template_id: formData.template_id,
        language: formData.language,
        
        // Conditions (legacy format for backward compatibility)
        conditions: {
          booking_sources: formData.booking_sources,
          require_valid_phone: formData.require_valid_phone,
          require_booking_active: formData.require_booking_active,
          require_booking_not_cancelled: formData.require_booking_not_cancelled,
          require_online_checkin_completed: formData.require_online_checkin_completed,
          require_entry_code_available: formData.require_entry_code_available,
          require_payment_completed: formData.require_payment_completed,
          require_assigned_room: formData.require_assigned_room,
          min_nights: formData.min_nights ? parseInt(formData.min_nights) : null,
          max_nights: formData.max_nights ? parseInt(formData.max_nights) : null,
          min_amount: formData.min_amount ? parseFloat(formData.min_amount) : null,
          max_amount: formData.max_amount ? parseFloat(formData.max_amount) : null,
          languages: formData.languages,
          nationalities: formData.nationalities,
          units: formData.unit_ids,
        },
        conditions_config: {
          booking_sources: formData.booking_sources,
          require_valid_phone: formData.require_valid_phone,
          require_booking_active: formData.require_booking_active,
          require_booking_not_cancelled: formData.require_booking_not_cancelled,
          require_online_checkin_completed: formData.require_online_checkin_completed,
          require_entry_code_available: formData.require_entry_code_available,
          require_payment_completed: formData.require_payment_completed,
          require_assigned_room: formData.require_assigned_room,
          min_nights: formData.min_nights ? parseInt(formData.min_nights) : null,
          max_nights: formData.max_nights ? parseInt(formData.max_nights) : null,
          min_amount: formData.min_amount ? parseFloat(formData.min_amount) : null,
          max_amount: formData.max_amount ? parseFloat(formData.max_amount) : null,
          languages: formData.languages,
          nationalities: formData.nationalities,
          units: formData.unit_ids,
        },
        
        // Quiet Hours
        quiet_hours: {
          enabled: formData.quiet_hours_enabled,
          use_global: formData.quiet_hours_use_global,
          start_time: formData.quiet_hours_start,
          end_time: formData.quiet_hours_end,
          start: formData.quiet_hours_start,
          end: formData.quiet_hours_end,
        },
        
        // Dependencies
        depends_on: formData.depends_on,
        
        // Retry
        retry_policy: {
          max_retries: parseInt(formData.retry_attempts) || 3,
          retry_delay_minutes: parseInt(formData.retry_backoff) || 30,
        },
        retry_config: {
          max_attempts: parseInt(formData.retry_attempts) || 3,
          backoff_minutes: parseInt(formData.retry_backoff) || 30,
          timeout_minutes: parseInt(formData.retry_timeout) || 60,
        },
        
        // Simulation
        simulation_mode: formData.simulation_mode,
      };
      
      await onSave(payload, rule?.id);
      onClose();
    } catch (error) {
      console.error('Error saving rule:', error);
    } finally {
      setSaving(false);
    }
  };

  const selectedTemplate = templates.find(t => t.id === formData.template_id);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#C5A059]" />
            {rule ? 'Modifica Regola' : 'Nuova Regola di Comunicazione'}
          </DialogTitle>
          <DialogDescription>
            Configura tutti i parametri della regola di automazione
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-7 w-full">
            <TabsTrigger value="general" className="text-xs">Generale</TabsTrigger>
            <TabsTrigger value="timing" className="text-xs">Timing</TabsTrigger>
            <TabsTrigger value="channels" className="text-xs">Canali</TabsTrigger>
            <TabsTrigger value="conditions" className="text-xs">Condizioni</TabsTrigger>
            <TabsTrigger value="quiet" className="text-xs">Quiet Hours</TabsTrigger>
            <TabsTrigger value="advanced" className="text-xs">Avanzate</TabsTrigger>
            <TabsTrigger value="test" className="text-xs">Test</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto py-4">
            {/* GENERAL TAB */}
            <TabsContent value="general" className="space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="name">Nome Regola *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="es. Conferma Prenotazione Automatica"
                    data-testid="rule-name-input"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="description">Descrizione</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Descrizione opzionale della regola..."
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Evento Trigger *</Label>
                  <Select value={formData.event} onValueChange={(v) => updateField('event', v)}>
                    <SelectTrigger data-testid="rule-event-select">
                      <SelectValue placeholder="Seleziona evento" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(EVENT_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Priorità (1-10)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.priority}
                    onChange={(e) => updateField('priority', e.target.value)}
                    data-testid="rule-priority-input"
                  />
                </div>

                <div>
                  <Label>Template Messaggio *</Label>
                  <Select value={formData.template_id} onValueChange={(v) => updateField('template_id', v)}>
                    <SelectTrigger data-testid="rule-template-select">
                      <SelectValue placeholder="Seleziona template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((tpl) => (
                        <SelectItem key={tpl.id} value={tpl.id}>{tpl.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Lingua</Label>
                  <Select value={formData.language} onValueChange={(v) => updateField('language', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="it">Italiano</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="de">Deutsch</SelectItem>
                      <SelectItem value="fr">Français</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Template Preview */}
              {selectedTemplate && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium">Anteprima Template</span>
                  </div>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap bg-white p-3 rounded border">
                    {selectedTemplate.body || 'Nessun contenuto'}
                  </div>
                </div>
              )}

              {/* Enable/Disable Toggles */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Regola Attiva</p>
                    <p className="text-xs text-gray-500">La regola verrà eseguita automaticamente</p>
                  </div>
                  <Switch
                    checked={formData.enabled}
                    onCheckedChange={(v) => updateField('enabled', v)}
                    data-testid="rule-enabled-toggle"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Modalità Simulazione</p>
                    <p className="text-xs text-gray-500">I messaggi verranno simulati, non inviati realmente</p>
                  </div>
                  <Switch
                    checked={formData.simulation_mode}
                    onCheckedChange={(v) => updateField('simulation_mode', v)}
                    data-testid="rule-simulation-toggle"
                  />
                </div>
              </div>
            </TabsContent>

            {/* TIMING TAB */}
            <TabsContent value="timing" className="space-y-4 mt-0">
              <div>
                <Label>Modalità Esecuzione</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {Object.entries(EXECUTION_MODE_LABELS).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => updateField('execution_mode', value)}
                      className={`p-3 rounded-lg border text-sm transition-colors ${
                        formData.execution_mode === value
                          ? 'bg-[#C5A059] text-white border-[#C5A059]'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-[#C5A059]'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Immediate Mode */}
              {formData.execution_mode === 'immediate' && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-3">Delay dopo l&apos;evento</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Giorni</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.delay_days}
                        onChange={(e) => updateField('delay_days', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Ore</Label>
                      <Input
                        type="number"
                        min="0"
                        max="23"
                        value={formData.delay_hours}
                        onChange={(e) => updateField('delay_hours', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Minuti</Label>
                      <Input
                        type="number"
                        min="0"
                        max="59"
                        value={formData.delay_minutes}
                        onChange={(e) => updateField('delay_minutes', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Relative Mode */}
              {formData.execution_mode === 'relative' && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-medium text-green-900 mb-3">Esecuzione Relativa</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Evento di Riferimento</Label>
                      <Select value={formData.reference_event} onValueChange={(v) => updateField('reference_event', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="arrival">Arrivo</SelectItem>
                          <SelectItem value="departure">Partenza</SelectItem>
                          <SelectItem value="booking_created">Prenotazione</SelectItem>
                          <SelectItem value="checkin">Check-in</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Offset (giorni)</Label>
                      <Input
                        type="number"
                        value={formData.offset_days}
                        onChange={(e) => updateField('offset_days', e.target.value)}
                        placeholder="-3 = 3 giorni prima"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Negativo = prima, Positivo = dopo
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Fixed Time Mode */}
              {formData.execution_mode === 'fixed_time' && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-medium text-purple-900 mb-3">Orario Fisso</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Orario Esecuzione</Label>
                      <Input
                        type="time"
                        value={formData.fixed_time}
                        onChange={(e) => updateField('fixed_time', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Timezone</Label>
                      <Select value={formData.timezone} onValueChange={(v) => updateField('timezone', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Europe/Rome">Europe/Rome (CET)</SelectItem>
                          <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
                          <SelectItem value="America/New_York">America/New_York (EST)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* CRITICAL: Missed Execution Policy */}
              <Alert className="border-amber-300 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <strong>IMPORTANTE:</strong> Se la prenotazione arriva DOPO l&apos;orario configurato
                </AlertDescription>
              </Alert>

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-medium text-amber-900 mb-3 flex items-center gap-2">
                  <Timer className="w-4 h-4" />
                  Policy Esecuzione Mancata
                </h4>
                <div className="space-y-3">
                  {Object.entries(MISSED_POLICY_LABELS).map(([value, label]) => (
                    <label key={value} className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="missed_policy"
                        value={value}
                        checked={formData.missed_policy === value}
                        onChange={() => updateField('missed_policy', value)}
                        className="mt-1"
                      />
                      <div>
                        <span className="font-medium">{label}</span>
                        {value === 'send_after_delay' && formData.missed_policy === 'send_after_delay' && (
                          <div className="flex items-center gap-2 mt-1">
                            <Input
                              type="number"
                              min="0"
                              value={formData.missed_delay_minutes}
                              onChange={(e) => updateField('missed_delay_minutes', e.target.value)}
                              className="w-20 h-8"
                            />
                            <span className="text-sm text-gray-600">minuti</span>
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-amber-700 mt-3">
                  Esempio: Messaggio arrivo configurato ore 09:00, prenotazione arriva alle 14:35 
                  → Con &quot;Invia dopo 2 minuti&quot; il messaggio parte alle 14:37
                </p>
              </div>
            </TabsContent>

            {/* CHANNELS TAB */}
            <TabsContent value="channels" className="space-y-4 mt-0">
              <div>
                <Label>Canali di Comunicazione</Label>
                <p className="text-xs text-gray-500 mb-3">Seleziona uno o più canali per l&apos;invio</p>
                <div className="grid grid-cols-2 gap-3">
                  {['whatsapp', 'push', 'email', 'sms'].map((ch) => {
                    const Icon = CHANNEL_ICONS[ch];
                    const isSelected = formData.channels.includes(ch);
                    return (
                      <button
                        key={ch}
                        type="button"
                        onClick={() => toggleArrayField('channels', ch)}
                        className={`p-4 rounded-lg border flex items-center gap-3 transition-colors ${
                          isSelected 
                            ? 'bg-[#C5A059] text-white border-[#C5A059]' 
                            : 'bg-white text-gray-700 border-gray-300 hover:border-[#C5A059]'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <div className="text-left">
                          <span className="font-medium capitalize">{ch}</span>
                          {ch === 'sms' && <span className="text-xs block opacity-70">Coming soon</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {formData.channels.length > 1 && (
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <Label>Ordine Fallback</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Se il canale primario fallisce, prova con il successivo
                  </p>
                  <div className="flex items-center gap-2">
                    {formData.channels.map((ch, idx) => (
                      <span key={ch}>
                        <Badge variant={idx === 0 ? "default" : "outline"}>
                          {idx + 1}. {ch}
                        </Badge>
                        {idx < formData.channels.length - 1 && <span className="mx-1">→</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* CONDITIONS TAB */}
            <TabsContent value="conditions" className="space-y-4 mt-0">
              {/* Booking Sources */}
              <div>
                <Label>Fonte Prenotazione</Label>
                <p className="text-xs text-gray-500 mb-2">Lascia vuoto per tutte le fonti</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(BOOKING_SOURCE_LABELS).map(([value, label]) => {
                    const isSelected = formData.booking_sources.includes(value);
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => toggleArrayField('booking_sources', value)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                          isSelected 
                            ? 'bg-[#C5A059] text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Requirements */}
              <div className="p-4 bg-gray-50 rounded-lg border">
                <Label className="mb-3 block">Requisiti</Label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { field: 'require_valid_phone', label: 'Telefono valido', icon: Phone },
                    { field: 'require_booking_active', label: 'Prenotazione attiva', icon: CheckCircle },
                    { field: 'require_booking_not_cancelled', label: 'Non cancellata', icon: Shield },
                    { field: 'require_online_checkin_completed', label: 'Check-in online completato', icon: Users },
                    { field: 'require_entry_code_available', label: 'Codice accesso disponibile', icon: Target },
                    { field: 'require_payment_completed', label: 'Pagamento completato', icon: CreditCard },
                    { field: 'require_assigned_room', label: 'Alloggio assegnato', icon: Home },
                  ].map(({ field, label, icon: Icon }) => (
                    <label key={field} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData[field]}
                        onChange={(e) => updateField(field, e.target.checked)}
                        className="rounded border-gray-300 text-[#C5A059] focus:ring-[#C5A059]"
                      />
                      <Icon className="w-4 h-4 text-gray-500" />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Numeric Filters */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Notti Minime</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.min_nights}
                    onChange={(e) => updateField('min_nights', e.target.value)}
                    placeholder="Nessun limite"
                  />
                </div>
                <div>
                  <Label>Notti Massime</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.max_nights}
                    onChange={(e) => updateField('max_nights', e.target.value)}
                    placeholder="Nessun limite"
                  />
                </div>
                <div>
                  <Label>Importo Minimo (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.min_amount}
                    onChange={(e) => updateField('min_amount', e.target.value)}
                    placeholder="Nessun limite"
                  />
                </div>
                <div>
                  <Label>Importo Massimo (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.max_amount}
                    onChange={(e) => updateField('max_amount', e.target.value)}
                    placeholder="Nessun limite"
                  />
                </div>
              </div>

              {/* Units Filter */}
              {units && units.length > 0 && (
                <div>
                  <Label>Alloggi</Label>
                  <p className="text-xs text-gray-500 mb-2">Lascia vuoto per tutti gli alloggi</p>
                  <div className="flex flex-wrap gap-2">
                    {units.map((unit) => {
                      const isSelected = formData.unit_ids.includes(unit.id);
                      return (
                        <button
                          key={unit.id}
                          type="button"
                          onClick={() => toggleArrayField('unit_ids', unit.id)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            isSelected 
                              ? 'bg-[#C5A059] text-white' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {unit.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* QUIET HOURS TAB */}
            <TabsContent value="quiet" className="space-y-4 mt-0">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🌙</span>
                  <div>
                    <p className="font-medium">Quiet Hours</p>
                    <p className="text-xs text-gray-500">Non inviare messaggi durante questi orari</p>
                  </div>
                </div>
                <Switch
                  checked={formData.quiet_hours_enabled}
                  onCheckedChange={(v) => updateField('quiet_hours_enabled', v)}
                />
              </div>

              {formData.quiet_hours_enabled && (
                <>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="text-sm">Usa impostazioni globali</span>
                    <Switch
                      checked={formData.quiet_hours_use_global}
                      onCheckedChange={(v) => updateField('quiet_hours_use_global', v)}
                    />
                  </div>

                  {!formData.quiet_hours_use_global && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Inizio (es. 22:00)</Label>
                        <Input
                          type="time"
                          value={formData.quiet_hours_start}
                          onChange={(e) => updateField('quiet_hours_start', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Fine (es. 08:00)</Label>
                        <Input
                          type="time"
                          value={formData.quiet_hours_end}
                          onChange={(e) => updateField('quiet_hours_end', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* ADVANCED TAB */}
            <TabsContent value="advanced" className="space-y-4 mt-0">
              {/* Dependencies */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-3">
                  <GitBranch className="w-4 h-4 text-purple-600" />
                  <Label className="mb-0">Dipendenze</Label>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  Esegui solo dopo che queste regole sono state completate
                </p>
                <div className="space-y-2">
                  {allRules.filter(r => r.id !== rule?.id).map((r) => {
                    const isSelected = formData.depends_on.includes(r.id);
                    return (
                      <label key={r.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleArrayField('depends_on', r.id)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm">{r.name}</span>
                        <Badge variant="outline" className="text-xs">{EVENT_LABELS[r.event] || r.event}</Badge>
                      </label>
                    );
                  })}
                  {allRules.filter(r => r.id !== rule?.id).length === 0 && (
                    <p className="text-sm text-gray-500 italic">Nessuna altra regola disponibile</p>
                  )}
                </div>
              </div>

              {/* Retry Configuration */}
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-2 mb-3">
                  <RefreshCw className="w-4 h-4 text-orange-600" />
                  <Label className="mb-0">Retry Policy</Label>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Tentativi</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={formData.retry_attempts}
                      onChange={(e) => updateField('retry_attempts', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Backoff (min)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.retry_backoff}
                      onChange={(e) => updateField('retry_backoff', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Timeout (min)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.retry_timeout}
                      onChange={(e) => updateField('retry_timeout', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* TEST TAB */}
            <TabsContent value="test" className="space-y-4 mt-0">
              <Alert>
                <TestTube className="h-4 w-4" />
                <AlertDescription>
                  Testa la regola con una prenotazione specifica per verificare matching e output
                </AlertDescription>
              </Alert>

              <div className="p-4 bg-gray-50 rounded-lg border">
                <p className="text-sm text-gray-600 mb-4">
                  Salva prima la regola, poi potrai testarla dalla lista principale cliccando l&apos;icona <TestTube className="w-4 h-4 inline" />
                </p>
                
                <div className="space-y-2 text-sm">
                  <p><strong>Evento:</strong> {EVENT_LABELS[formData.event]}</p>
                  <p><strong>Canali:</strong> {formData.channels.join(', ')}</p>
                  <p><strong>Template:</strong> {selectedTemplate?.name || 'Non selezionato'}</p>
                  <p><strong>Timing:</strong> {EXECUTION_MODE_LABELS[formData.execution_mode]}</p>
                  <p><strong>Missed Policy:</strong> {MISSED_POLICY_LABELS[formData.missed_policy]}</p>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="rule-save-btn">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {rule ? 'Salva Modifiche' : 'Crea Regola'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// TEST RULE MODAL
// ============================================================================

const TestRuleModal = ({ isOpen, onClose, rule, token }) => {
  const [bookingId, setBookingId] = useState('');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const runTest = async () => {
    if (!bookingId.trim()) {
      toast.error('Inserisci un ID prenotazione');
      return;
    }

    setTesting(true);
    setResult(null);

    try {
      const response = await axios.post(
        `${API}/admin/communication/rules/${rule.id}/test`,
        { booking_id: bookingId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
    } catch (error) {
      toast.error('Errore nel test');
      setResult({ error: error.response?.data?.detail || 'Test fallito' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TestTube className="w-5 h-5 text-blue-600" />
            Test Regola: {rule?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>ID Prenotazione</Label>
            <div className="flex gap-2">
              <Input
                value={bookingId}
                onChange={(e) => setBookingId(e.target.value)}
                placeholder="Inserisci booking ID o codice"
              />
              <Button onClick={runTest} disabled={testing}>
                {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Testa'}
              </Button>
            </div>
          </div>

          {result && (
            <div className={`p-4 rounded-lg border ${result.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              {result.error ? (
                <div className="text-red-700">
                  <strong>Errore:</strong> {result.error}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    {result.matched ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <X className="w-5 h-5 text-red-600" />
                    )}
                    <span className="font-medium">
                      {result.matched ? 'Prenotazione MATCHATA' : 'Prenotazione NON matchata'}
                    </span>
                  </div>

                  {result.exclusion_reason && (
                    <p className="text-sm text-gray-600">
                      <strong>Motivo esclusione:</strong> {result.exclusion_reason}
                    </p>
                  )}

                  {result.scheduled_at && (
                    <p className="text-sm">
                      <strong>Esecuzione calcolata:</strong> {new Date(result.scheduled_at).toLocaleString('it-IT')}
                    </p>
                  )}

                  {result.rendered_message && (
                    <div>
                      <strong className="text-sm">Messaggio renderizzato:</strong>
                      <pre className="mt-1 p-3 bg-white rounded border text-sm whitespace-pre-wrap">
                        {result.rendered_message}
                      </pre>
                    </div>
                  )}

                  {result.channel && (
                    <p className="text-sm">
                      <strong>Canale:</strong> {result.channel}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function RulesPage() {
  const { token } = useAuth();
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testingRule, setTestingRule] = useState(null);
  const [filterEvent, setFilterEvent] = useState('all');

  const fetchRules = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/communication/rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRules(data.rules || []);
      }
    } catch (error) {
      console.error('Error fetching rules:', error);
      toast.error('Errore nel caricamento regole');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/communication/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, [token]);

  const fetchUnits = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/units`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnits(data || []);
      }
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchRules();
      fetchTemplates();
      fetchUnits();
    }
  }, [token, fetchRules, fetchTemplates, fetchUnits]);

  const handleToggle = async (rule) => {
    try {
      const response = await fetch(`${API}/admin/communication/rules/${rule.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled: !rule.enabled })
      });
      
      if (response.ok) {
        toast.success(rule.enabled ? 'Regola disattivata' : 'Regola attivata');
        fetchRules();
      }
    } catch (error) {
      toast.error('Errore nel cambio stato');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!confirm('Eliminare questa regola?')) return;
    
    try {
      const response = await fetch(`${API}/admin/communication/rules/${ruleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Regola eliminata');
        fetchRules();
      }
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleSave = async (data, ruleId) => {
    try {
      const url = ruleId 
        ? `${API}/admin/communication/rules/${ruleId}`
        : `${API}/admin/communication/rules`;
      
      const response = await fetch(url, {
        method: ruleId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        toast.success(ruleId ? 'Regola aggiornata' : 'Regola creata');
        fetchRules();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore nel salvataggio');
      throw error;
    }
  };

  const handleDuplicate = (rule) => {
    const duplicated = {
      ...rule,
      id: undefined,
      name: `${rule.name} (copia)`,
    };
    setEditingRule(duplicated);
    setModalOpen(true);
  };

  const handleTest = (rule) => {
    setTestingRule(rule);
    setTestModalOpen(true);
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const response = await fetch(`${API}/admin/seed-automation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.rules_created} regole e ${data.templates_created} template creati!`);
        fetchRules();
        fetchTemplates();
      } else {
        toast.error('Errore nel seed');
      }
    } catch (error) {
      toast.error('Errore nel seed');
    } finally {
      setSeeding(false);
    }
  };

  // Filter rules
  const filteredRules = rules.filter(rule => {
    if (filterEvent === 'all') return true;
    return rule.event === filterEvent;
  });

  // Group rules by event
  const rulesByEvent = filteredRules.reduce((acc, rule) => {
    const event = rule.event || 'other';
    if (!acc[event]) acc[event] = [];
    acc[event].push(rule);
    return acc;
  }, {});

  return (
    <AdminLayout title="Communication Rules">
      <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto" data-testid="rules-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Regole di Comunicazione</h1>
            <p className="text-gray-500 mt-1">Configurazione completa delle automazioni messaggi</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchRules}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Aggiorna
            </Button>
            <Button 
              onClick={() => { setEditingRule(null); setModalOpen(true); }} 
              data-testid="new-rule-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nuova Regola
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <Select value={filterEvent} onValueChange={setFilterEvent}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filtra per evento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli eventi</SelectItem>
                {Object.entries(EVENT_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-gray-500">
            {filteredRules.length} regole {filterEvent !== 'all' && `per "${EVENT_LABELS[filterEvent]}"`}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : rules.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuna regola configurata</h3>
            <p className="text-gray-500 mb-6">Le regole permettono di automatizzare l&apos;invio di messaggi basati su eventi</p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={handleSeedDefaults} disabled={seeding} variant="outline" data-testid="seed-defaults-btn">
                {seeding ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                Carica Regole Default
              </Button>
              <Button onClick={() => { setEditingRule(null); setModalOpen(true); }} data-testid="create-first-rule-btn">
                <Plus className="w-4 h-4 mr-2" />
                Crea Prima Regola
              </Button>
            </div>

            {/* Default Rules Preview */}
            <div className="mt-8 pt-8 border-t">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Regole Default Disponibili</h4>
              <div className="flex flex-wrap justify-center gap-2">
                {DEFAULT_RULES.map((r, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {r.name}
                  </Badge>
                ))}
              </div>
            </div>
          </Card>
        ) : (
          <>
            {/* Summary Banner */}
            <div className="bg-gradient-to-r from-[#C5A059]/10 to-[#C5A059]/5 rounded-xl border border-[#C5A059]/20 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#C5A059]/20 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{rules.length} Regole Configurate</p>
                    <p className="text-sm text-gray-500">
                      {rules.filter(r => r.enabled).length} attive • 
                      {rules.filter(r => r.simulation_mode).length} in simulazione
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleSeedDefaults} disabled={seeding}>
                  {seeding ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                  Aggiungi Default
                </Button>
              </div>
            </div>

            {/* Rules Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRules.map(rule => {
                const sameEventRules = rules.filter(r => 
                  r.event === rule.event && 
                  r.enabled && 
                  r.id !== rule.id
                );
                const hasOverlap = sameEventRules.length > 0 && rule.enabled;
                const overlapInfo = hasOverlap 
                  ? `${sameEventRules.length} altra regola per "${EVENT_LABELS[rule.event] || rule.event}"`
                  : '';
                
                return (
                  <RuleCard
                    key={rule.id}
                    rule={rule}
                    onEdit={(r) => { setEditingRule(r); setModalOpen(true); }}
                    onDelete={handleDelete}
                    onToggle={handleToggle}
                    onDuplicate={handleDuplicate}
                    onTest={handleTest}
                    hasOverlap={hasOverlap}
                    overlapInfo={overlapInfo}
                  />
                );
              })}
            </div>
          </>
        )}

        {/* Rule Editor Modal */}
        <RuleEditorModal
          isOpen={modalOpen}
          onClose={() => { setModalOpen(false); setEditingRule(null); }}
          rule={editingRule}
          templates={templates}
          units={units}
          allRules={rules}
          onSave={handleSave}
        />

        {/* Test Rule Modal */}
        <TestRuleModal
          isOpen={testModalOpen}
          onClose={() => { setTestModalOpen(false); setTestingRule(null); }}
          rule={testingRule}
          token={token}
        />
      </div>
    </AdminLayout>
  );
}
